import {
  Card,
  CardBody,
  Row,
  Col,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  Breadcrumb,
  BreadcrumbItem,
} from "reactstrap";
import {
  FaCheckCircle,
  FaFolderOpen,
  FaScrewdriver,
  FaTasks,
  FaShieldAlt,
  FaVideo,
} from "react-icons/fa";
import ReactTable from "components/ReactTable/ReactTable.js";
import { useMemo, useState, React, useEffect, useContext } from "react";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import { MainContext } from "context/MainContext.js";
import LoadingOverlay from "react-loading-overlay";
import { faHome } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Utilities from "components/Utils/utilities";
import { useTranslation } from "react-i18next";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

const SiteList = () => {
  const history = useHistory();
  const location = useLocation();
  const privileges = useContext(MainContext);
  const [alert, setAlert] = useState(null);
  const [forceRefreshCount, setForceRefreshCount] = useState(0);
  const [showLoading, setShowLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (!Utilities.hasAccess(privileges["installations.site.list"])) {
      history.push({
        pathname: "/",
      });
    } else if (Utilities.hasAgreedTerms() == "false") {
      history.push({
        pathname: "/auth/terms",
      });
    }
  }, []);

  const [params, setParams] = useState(location.state?.value ?? "");

  const redirectToProfiles = (siteId, siteName) => {
    const location = {
      pathname: "/admin/installations/profile/list",
      state: {
        siteId: siteId,
        siteName: siteName,
      },
    };
    history.push(location);
  };

  const redirectToDetails = (siteId, status) => {
    const location = {
      pathname: "/admin/installations/site/details",
      state: {
        siteId: siteId,
        status: status,
      },
    };
    history.push(location);
  };

  const exportEndpoints = (siteId) => {
    fetch("/api/v1/site/room/export", {
      method: "GET",
      headers: {
        sessionId: sessionStorage.getItem("sessionId"),
        siteId: siteId,
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Export failed: " + response.statusText);
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "rooms_export.xlsx");
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const redirectToImportRooms = (siteId) => {
    const location = {
      pathname: "/admin/installations/site/endpoint/import",
      state: {
        value: siteId,
      },
    };
    history.push(location);
  };

  const redirectToEdit = (siteName, siteId) => {
    const location = {
      pathname: "/admin/installations/site/edit",
      state: {
        value: siteId ? siteId : "",
        name: siteName ? siteName : "",
        siteName: "",
        mode: siteId ? "edit" : "create",
      },
    };
    history.push(location);
  };

  const deleteConfirm = (name) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t(
          "table.row.actions.deletesite.t.alert.question." +
            sessionStorage.getItem("bundleType"),
          "Are you sure you want to delete this site?",
        )}
        onConfirm={() => {
          deleteSite(name);
        }}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="danger"
        cancelBtnBsStyle="info"
        confirmBtnText="Yes, delete it!"
        cancelBtnText="Cancel"
        showCancel
        btnSize=""
      >
        {t(
          "table.row.actions.deletesite.t.alert.subquestion." +
            sessionStorage.getItem("bundleType"),
          "You will not be able to recover this site",
        )}{" "}
        '{name}'
      </ReactBSAlert>,
    );
  };

  const deleteSite = (name) => {
    const start = Date.now();

    // Call the API
    fetch("/api/v1/site/delete", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        name: name,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        if (actualData.error_code) {
          errorDelete(actualData.error_code[0]);
        } else {
          successDelete();
          setForceRefreshCount((prevValue) => ++prevValue);
        }
      })
      .catch((err) => {
        console.log(err.message);
      });
  };

  const successDelete = () => {
    setAlert(
      <ReactBSAlert
        success
        style={{ display: "block", marginTop: "-100px" }}
        title="Deleted!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {t(
          "table.row.actions.deletesite.t.alert.success." +
            sessionStorage.getItem("bundleType"),
          "The site has been deleted",
        )}
      </ReactBSAlert>,
    );
  };

  const errorDelete = (errorMessage) => {
    setAlert(
      <ReactBSAlert
        error
        style={{ display: "block", marginTop: "-100px" }}
        title="Error!"
        onConfirm={() => hideAlert()}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="info"
        btnSize=""
      >
        {errorMessage}
      </ReactBSAlert>,
    );
  };

  const changeSiteStatus = (name, newStatus) => {
    setShowLoading(true);
    // Call the API
    fetch("/api/v1/site/status", {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        sessionId: sessionStorage.getItem("sessionId"),
      },
      body: JSON.stringify({
        name: name,
        newStatus: newStatus,
      }),
    })
      .then((response) => response.json())
      .then((actualData) => {
        setShowLoading(false);
        if (actualData.error_code) {
          toast.error(actualData.error_code[0], {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
        } else {
          toast.success("Site changed successfully", {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: false,
            draggable: false,
            theme: "colored",
          });
          setForceRefreshCount((prevValue) => ++prevValue);
        }

        setForceRefreshCount((prevValue) => ++prevValue);
      })
      .catch((err) => {
        setShowLoading(false);
        console.log(err.message);
      });
  };

  const hideAlert = () => {
    setAlert(null);
  };

  const redirectToDeviceList = (siteName) => {
    const location = {
      pathname: "/admin/devices/list",
      state: { value: "site||" + siteName },
    };
    history.push(location);
  };

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "status",
        sortBy: "status",
        flex: "1 1 0",
        Cell: (props) => {
          const tooltipId = `vmElement-${props.row.original.id}`;

          var icon = null;
          var value = null;

          if (props.value == "NEW") {
            icon = <FaFolderOpen className="tableIconFontSize" />;
            value = "New";
          } else if (props.value == "IN_PROGRESS") {
            icon = <FaScrewdriver className="tableIconFontSize" />;
            value = "Provisioning in progress";
          } else if (props.value == "VERIFICATION") {
            icon = <FaTasks className="tableIconFontSize" />;
            value = "Verification in progress";
          } else if (props.value == "VERIFIED") {
            icon = <FaShieldAlt className="tableIconFontSize" />;
            value = "Verified";
          } else if (props.value == "MONITORING") {
            icon = <FaVideo className="tableIconFontSize" />;
            value = "Monitoring";
          }
          return (
            <>
              <a id={tooltipId} className="vmElement">
                {icon}
              </a>{" "}
              {props.row.original.status === "VERIFICATION" && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "2px 7px",
                    borderRadius: 20,
                    background: "#E6F1FB",
                    color: "#0C447C",
                  }}
                >
                  {props.row.original.percentPostProvisioningDone}%
                </span>
              )}
              {props.row.original.status === "IN_PROGRESS" && (
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "2px 7px",
                    borderRadius: 20,
                    background: "#E6F1FB",
                    color: "#0C447C",
                  }}
                >
                  {props.row.original.percentProvisioningDone}%
                </span>
              )}
              <ReactTooltip
                anchorSelect={`#${tooltipId}`}
                place="top"
                style={{ textAlign: "left", zIndex: "100" }}
              >
                <div>{value}</div>
              </ReactTooltip>
            </>
          );
        },
      },
      {
        Header: "Name",
        accessor: "name",
        sortBy: "name",
        flex: "1 1 0",
        Cell: (props) => {
          if (
            props.row.original.status != "MONITORING" &&
            props.row.original.status != "VERIFIED"
          ) {
            {
              return (
                <a
                  onClick={() =>
                    redirectToEdit(
                      props.row.original.name,
                      props.row.original.id,
                    )
                  }
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {props.value}
                </a>
              );
            }
          } else if (props.row.original.status == "MONITORING") {
            {
              return (
                <a
                  onClick={() => redirectToDetails(props.row.original.id)}
                  style={{
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  {props.value}
                </a>
              );
            }
          } else {
            return props.value;
          }
        },
      },
      {
        Header: "Devices",
        accessor: "deviceCount",
        flex: "1 1 0",
        sortable: false,
        Cell: (props) => {
          return (
            <a
              onClick={() => redirectToDeviceList(props.row.original.id)}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
      },
      {
        Header: "Endpoints",
        accessor: "roomCount",
        flex: "1 1 0",
        Cell: (props) => {
          return (
            <a
              onClick={() => redirectToDetails(props.row.original.id, "all")}
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        sortable: false,
      },
      {
        Header: "Installation Done",
        accessor: "installationDone",
        flex: "1 1 0",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToDetails(props.row.original.id, "configured")
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        sortable: false,
      },
      {
        Header: "Installation Requiring Attention",
        accessor: "installationAttention",
        flex: "1 1 0",
        Cell: (props) => {
          return (
            <a
              onClick={() =>
                redirectToDetails(props.row.original.id, "attention")
              }
              style={{
                cursor: "pointer",
                textDecoration: "underline",
              }}
            >
              {props.value}
            </a>
          );
        },
        sortable: false,
      },
      {
        Header: "Expected GAMs",
        accessor: "expectedGAMs",
        sortBy: "expectedGAMs",
        flex: "1 1 0",
      },
      {
        Header: "Track Crew",
        accessor: "trackCrew",
        sortBy: "trackCrew",
        Cell: (props) => {
          return props.value ? (
            <FaCheckCircle className="text-primary tableIconFontSize" />
          ) : (
            ""
          );
        },
        flex: "1 1 0",
      },
      {
        Header: "",
        accessor: "actions",
        Cell: (props) => {
          return (
            <UncontrolledDropdown direction="down">
              <DropdownToggle
                caret
                color="info"
                style={{
                  padding: "12px 7px",
                }}
              >
                Actions
              </DropdownToggle>
              <DropdownMenu container={"body"}>
                {/* ------------------- SHOW DETAILS -------------------*/}
                <DropdownItem
                  onClick={() => redirectToDetails(props.row.original.id)}
                >
                  Show Details
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    redirectToProfiles(
                      props.row.original.id,
                      props.row.original.name,
                    )
                  }
                >
                  Show Profiles
                </DropdownItem>
                <DropdownItem divider />

                {/* ------------------- IMPORT/EXPORT ENDPOINTS -------------------*/}
                {props.row.original.status != "NEW" &&
                props.row.original.status != "IN_PROGRESS" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() => redirectToImportRooms(props.row.original.id)}
                  >
                    {t(
                      "table.row.actions.importendpoints.t." +
                        sessionStorage.getItem("bundleType"),
                      "Import Endpoints",
                    )}
                  </DropdownItem>
                )}

                {props.row.original.roomCount == 0 ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() => exportEndpoints(props.row.original.id)}
                  >
                    {t(
                      "table.row.actions.exportendpoints.t." +
                        sessionStorage.getItem("bundleType"),
                      "Export Endpoints",
                    )}
                  </DropdownItem>
                )}

                <DropdownItem divider />
                {/* ------------------- EDIT SITE -------------------*/}
                {props.row.original.status != "NEW" &&
                props.row.original.status != "IN_PROGRESS" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      redirectToEdit(
                        props.row.original.name,
                        props.row.original.id,
                      )
                    }
                  >
                    {t(
                      "table.row.actions.editsite.t." +
                        sessionStorage.getItem("bundleType"),
                      "Edit Site",
                    )}
                  </DropdownItem>
                )}

                {/* ------------------- DELETE SITE -------------------*/}
                <>
                  <DropdownItem
                    onClick={() => deleteConfirm(props.row.original.name)}
                  >
                    {t(
                      "table.row.actions.deletesite.t." +
                        sessionStorage.getItem("bundleType"),
                      "Delete Site",
                    )}
                  </DropdownItem>
                </>

                <DropdownItem divider />

                {/* ------------------- STOP PROVISIONING -------------------*/}
                {props.row.original.status != "IN_PROGRESS" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      changeSiteStatus(props.row.original.name, "NEW")
                    }
                  >
                    Stop Provisioning
                  </DropdownItem>
                )}

                {/* ------------------- START PROVISIONIONG -------------------*/}
                {props.row.original.status != "NEW" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      changeSiteStatus(props.row.original.name, "IN_PROGRESS")
                    }
                  >
                    Start Provisioning
                  </DropdownItem>
                )}

                {/* ------------------- STOP VERIFICATION -------------------*/}
                {props.row.original.status != "VERIFICATION" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      changeSiteStatus(props.row.original.name, "IN_PROGRESS")
                    }
                  >
                    Stop Verifications
                  </DropdownItem>
                )}

                {/* ------------------- START VERIFICATION -------------------*/}
                {props.row.original.status != "IN_PROGRESS" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      changeSiteStatus(props.row.original.name, "VERIFICATION")
                    }
                  >
                    Start Verifications
                  </DropdownItem>
                )}

                {props.row.original.status != "VERIFIED" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      changeSiteStatus(props.row.original.name, "VERIFICATION")
                    }
                  >
                    Restart Verifications
                  </DropdownItem>
                )}

                {/* ------------------- MONITORING -------------------*/}
                {props.row.original.status != "VERIFIED" ? (
                  ""
                ) : (
                  <DropdownItem
                    onClick={() =>
                      changeSiteStatus(props.row.original.name, "MONITORING")
                    }
                  >
                    Start Monitoring
                  </DropdownItem>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
        flex: "1 1 0",
        padding: "0px",
        sortable: false,
        filterable: false,
      },
    ],
    [],
  );

  return (
    <>
      <ToastContainer />
      <div className="content">
        <LoadingOverlay
          active={showLoading}
          spinner
          text="Loading"
          className={showLoading ? "loading" : ""}
        />
        {alert}
        <Row>
          <Col md="12">
            <Card>
              <CardBody>
                <Breadcrumb>
                  <BreadcrumbItem>
                    <NavLink
                      to={{
                        pathname: "/admin/dashboard",
                      }}
                    >
                      <FontAwesomeIcon icon={faHome} />
                    </NavLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem>Automations</BreadcrumbItem>
                  <BreadcrumbItem>
                    {t(
                      "sites.breadcrumb.sites.t." +
                        sessionStorage.getItem("bundleType"),
                      "Sites",
                    )}
                  </BreadcrumbItem>
                </Breadcrumb>
              </CardBody>
            </Card>
            <Card>
              <CardBody>
                <ReactTable
                  query="/api/v1/site/list"
                  columns={columns}
                  param={params}
                  title="sites"
                  className="-striped -highlight primary-pagination"
                  forceRefresh={forceRefreshCount}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SiteList;
